/**
 * Empreinte d'appareil (anti-triche compétition différée).
 *
 * Combine deux sources :
 *   - **Web**  : FingerprintJS OSS (visitorId stable ~70%-80% sur même
 *                navigateur, mêmes paramètres). MIT, exécuté côté client,
 *                aucune donnée envoyée à un tiers.
 *   - **Native** : Capacitor Device.getId() — UUID stable par installation
 *                  iOS/Android (réinitialisé seulement à réinstallation).
 *
 * Le résultat est mis en cache localStorage pour éviter de rejouer le
 * calcul à chaque session (coûteux, ~200ms).
 *
 * Finalité : RGPD intérêt légitime (équité des classements compétition).
 * Conservation côté backend : 13 mois. Cf. PrivacyPolicy.tsx §13.
 */

import { getNativeDeviceId, isNative, getPlatform, platformReady } from '../platform/capacitor';

const STORAGE_KEY = 'wt_neo_device_signature_v1';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours

export interface DeviceSignature {
  /** Identifiant FingerprintJS (web) — string base16 de 32 chars ou null */
  webVisitorId: string | null;
  /** Identifiant natif Capacitor — UUID ou null */
  nativeDeviceId: string | null;
  /** 'web' | 'ios' | 'android' */
  platform: 'web' | 'ios' | 'android';
  /** Timestamp de calcul */
  computedAt: number;
}

interface CacheEntry extends DeviceSignature {
  /** version de schéma, pour invalider sur breaking change */
  v: number;
}

const CACHE_VERSION = 1;

function loadCached(): DeviceSignature | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (entry.v !== CACHE_VERSION) return null;
    if (Date.now() - entry.computedAt > CACHE_TTL_MS) return null;
    return entry;
  } catch {
    return null;
  }
}

function saveCached(sig: DeviceSignature): void {
  try {
    const entry: CacheEntry = { ...sig, v: CACHE_VERSION };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage plein/désactivé → non bloquant
  }
}

/**
 * Récupère (ou calcule) la signature d'appareil courante.
 *
 * @param force - true pour recalculer même si une version cachée existe
 */
export async function getDeviceSignature(force = false): Promise<DeviceSignature> {
  await platformReady();

  if (!force) {
    const cached = loadCached();
    if (cached) return cached;
  }

  const platform = getPlatform();
  let webVisitorId: string | null = null;
  let nativeDeviceId: string | null = null;

  // 1) Native ID (silencieux si pas Capacitor)
  if (isNative()) {
    nativeDeviceId = await getNativeDeviceId();
  }

  // 2) Web fingerprint (toujours calculé, même en native — utile pour
  //    croiser les sessions native + navigateur d'un même utilisateur)
  try {
    const mod = await import('@fingerprintjs/fingerprintjs').catch(() => null);
    if (mod) {
      const fp = await mod.default.load();
      const result = await fp.get();
      webVisitorId = result.visitorId;
    }
  } catch {
    // Erreur silencieuse — le serveur disposera juste du nativeDeviceId
    // ou retombera sur l'IP/ASN (cf. backend).
  }

  const sig: DeviceSignature = {
    webVisitorId,
    nativeDeviceId,
    platform,
    computedAt: Date.now(),
  };
  saveCached(sig);
  return sig;
}

/**
 * Invalide la signature mise en cache. À appeler en cas de changement de
 * compte sur le même appareil (logout/login d'un autre pseudo) — utile
 * pour ne pas remonter une signature obsolète d'un précédent locataire.
 */
export function clearCachedSignature(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // non bloquant
  }
}
